import { useState, useEffect } from "react";
import { Smartphone, ShieldCheck, Store, CheckCircle2, ArrowRight, ArrowLeft } from "lucide-react";
import { CountryPhoneSelector } from "../../components/CountryPhoneSelector";
import PasswordInput from "../../components/PasswordInput";
import { buildBusinessUrl, displayBusinessUrl } from "../../utils/businessUrl";
import { COUNTRIES } from "../../services/countries/countryData";
import RegistrationService from "../../services/client/RegistrationService";
import { showError, showSuccess } from "../../components/alertas";
import turnlinkLogo from "../../assets/turnlinkLOGO.png";
import "./register.css";

function RegisterPage() {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);

  // Paso 1
  const [selectedCountry, setSelectedCountry] = useState("MX");
  const [phoneValue, setPhoneValue] = useState("");
  const [phoneError, setPhoneError] = useState("");

  // Paso 2
  const [code, setCode] = useState("");
  const [registrationToken, setRegistrationToken] = useState("");

  // Paso 3
  const [businessName, setBusinessName] = useState("");
  const [username, setUsername] = useState("");
  const [prefixUrl, setPrefixUrl] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  // Éxito
  const [createdSlug, setCreatedSlug] = useState("");

  // Quitar el loader inicial del index.html (overlay z-index:9999). Sin esto la
  // página queda tapada por el spinner aunque ya esté renderizada, como el resto
  // de páginas (BrandingPage, legales, NotFound) que también lo remueven al montar.
  useEffect(() => {
    document.getElementById("initial-loader")?.remove();
    document.body.classList.add("js-loaded");
  }, []);

  const country = COUNTRIES.find((c) => c.code === selectedCountry);
  const fullPhone = `${country?.phoneCode || ""}${phoneValue}`;

  const siteUrl = () => buildBusinessUrl(createdSlug);

  const errMsg = (error) => error.response?.data?.message || error.message || "Ocurrió un error";

  const handleRequestOtp = async () => {
    const expectedDigits = country?.phoneDigits || 10;
    if (phoneValue.replace(/\D/g, "").length < expectedDigits) {
      setPhoneError(`Ingresa un número válido de ${expectedDigits} dígitos`);
      return;
    }
    try {
      setLoading(true);
      await RegistrationService.requestOtp(fullPhone);
      showSuccess("Código enviado", "Revisa tu WhatsApp e ingresa el código.");
      setStep(2);
    } catch (error) {
      showError("No se pudo enviar el código", errMsg(error));
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async () => {
    if (code.trim().length < 6) {
      showError("Código incompleto", "El código tiene 6 dígitos.");
      return;
    }
    try {
      setLoading(true);
      const res = await RegistrationService.verifyOtp(fullPhone, code.trim());
      setRegistrationToken(res.registrationToken);
      setStep(3);
    } catch (error) {
      showError("Código inválido", errMsg(error));
    } finally {
      setLoading(false);
    }
  };

  const handleComplete = async () => {
    if (!businessName.trim() || !username.trim() || !prefixUrl.trim() || !password) {
      showError("Faltan datos", "Completa todos los campos.");
      return;
    }
    if (password.length < 6) {
      showError("Contraseña corta", "Usa al menos 6 caracteres.");
      return;
    }
    if (password !== confirmPassword) {
      showError("No coinciden", "Las contraseñas no coinciden.");
      return;
    }
    try {
      setLoading(true);
      const res = await RegistrationService.complete({
        phone: fullPhone,
        registrationToken,
        name: businessName.trim(),
        username: username.trim(),
        password,
        prefixUrl: prefixUrl.trim(),
      });
      setCreatedSlug(res.slug || prefixUrl.trim());
      setStep(4);
    } catch (error) {
      showError("No se pudo crear la cuenta", errMsg(error));
    } finally {
      setLoading(false);
    }
  };

  // Estilos en register.css (tema claro de marca); aquí solo estructura y copy.
  return (
    <div className="rg-root">
      <a href="/" className="tl-wordmark rg-logo" aria-label="Turnlink, inicio">
        <img src={turnlinkLogo} alt="" />
        <span>turn<em>link</em></span>
      </a>

      <div className="rg-card">
        {/* Encabezado con progreso */}
        <div className="rg-head">
          <h1>Crea tu cuenta</h1>
          <p className="rg-muted">{step < 4 ? `Paso ${step} de 3` : "¡Listo!"}</p>
          {step < 4 && (
            <div className="rg-progress" aria-hidden>
              {[1, 2, 3].map((n) => <span key={n} className={n <= step ? "is-done" : ""} />)}
            </div>
          )}
        </div>

        {/* Paso 1: teléfono */}
        {step === 1 && (
          <>
            <h2 className="rg-step-title"><Smartphone size={18} /> Verifica tu WhatsApp</h2>
            <p className="rg-muted rg-step-desc">
              Te enviaremos un código por WhatsApp. Tu número queda ligado a tu cuenta.
            </p>
            <CountryPhoneSelector
              selectedCountry={selectedCountry}
              onCountryChange={setSelectedCountry}
              phoneValue={phoneValue}
              onPhoneChange={setPhoneValue}
              phoneError={phoneError}
              setPhoneError={setPhoneError}
            />
            <button className="rg-btn rg-btn-primary" onClick={handleRequestOtp} disabled={loading}>
              {loading ? "Enviando..." : "Enviar código"} <ArrowRight size={16} />
            </button>
          </>
        )}

        {/* Paso 2: OTP */}
        {step === 2 && (
          <>
            <h2 className="rg-step-title"><ShieldCheck size={18} /> Ingresa el código</h2>
            <p className="rg-muted rg-step-desc">
              Enviado a <strong>{fullPhone}</strong> por WhatsApp.
            </p>
            <input
              className="app-form-control rg-input rg-input-code"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
              placeholder="••••••"
              inputMode="numeric"
              aria-label="Código de 6 dígitos"
            />
            <button className="rg-btn rg-btn-primary" onClick={handleVerifyOtp} disabled={loading}>
              {loading ? "Verificando..." : "Verificar"} <ArrowRight size={16} />
            </button>
            <button className="rg-btn rg-btn-ghost" onClick={() => setStep(1)} disabled={loading}>
              <ArrowLeft size={14} /> Cambiar número
            </button>
          </>
        )}

        {/* Paso 3: datos del negocio */}
        {step === 3 && (
          <>
            <h2 className="rg-step-title"><Store size={18} /> Datos de tu negocio</h2>
            <div className="rg-form">
              <div className="app-form-group rg-field">
                <label className="app-form-label" htmlFor="rg-business">Nombre del negocio</label>
                <input id="rg-business" className="app-form-control rg-input" value={businessName} onChange={(e) => setBusinessName(e.target.value)} placeholder="Ej: Barbería El Corte" />
              </div>
              <div className="app-form-group rg-field">
                <label className="app-form-label" htmlFor="rg-username">Usuario</label>
                <input id="rg-username" className="app-form-control rg-input font-monospace" value={username} onChange={(e) => setUsername(e.target.value.trim())} placeholder="Ej: elcorte" />
              </div>
              <div className="app-form-group rg-field">
                <label className="app-form-label" htmlFor="rg-prefix">URL de tu página</label>
                <input
                  id="rg-prefix"
                  className="app-form-control rg-input font-monospace"
                  value={prefixUrl}
                  onChange={(e) => setPrefixUrl(e.target.value.toLowerCase().replace(/[^a-z0-9]/g, ""))}
                  placeholder="elcorte"
                  maxLength={25}
                />
                <small className="rg-hint">{displayBusinessUrl(prefixUrl || "tu-negocio")}</small>
              </div>
              <div className="app-form-group rg-field">
                <label className="app-form-label" htmlFor="rg-password">Contraseña</label>
                <PasswordInput id="rg-password" className="app-form-control rg-input" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" />
              </div>
              <div className="app-form-group rg-field">
                <label className="app-form-label" htmlFor="rg-password-2">Confirmar contraseña</label>
                <PasswordInput id="rg-password-2" className="app-form-control rg-input" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} placeholder="••••••••" />
              </div>
            </div>
            <button className="rg-btn rg-btn-primary" onClick={handleComplete} disabled={loading}>
              {loading ? "Creando cuenta..." : "Crear cuenta"} <ArrowRight size={16} />
            </button>
          </>
        )}

        {/* Paso 4: éxito */}
        {step === 4 && (
          <div className="rg-success">
            <CheckCircle2 size={48} aria-hidden />
            <h2>¡Cuenta creada!</h2>
            <p className="rg-muted">
              Tu prueba gratis de 1 mes ya está activa. Inicia sesión en tu sitio para configurar tu negocio.
            </p>
            <a className="rg-btn rg-btn-primary" href={siteUrl()}>
              Ir a mi sitio <ArrowRight size={16} />
            </a>
            <p className="rg-muted rg-success-user">
              Usuario: <strong>{username}</strong>
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

export default RegisterPage;
